import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useToast } from '../contexts/ToastContext';
import LoadingSpinner from './LoadingSpinner';
import BaseModal from './common/BaseModal';
import './ProfileTabs.css';

const Profile = () => {
  const { currentUser, logout, updateUserPassword, updateUserEmail, reauthenticate } = useAuth();
  const { showSuccess, showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [userName, setUserName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailPasswordVerify, setEmailPasswordVerify] = useState('');
  const [showEmailPasswordModal, setShowEmailPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const navigate = useNavigate();
  
  // Check if user is authenticated with Google
  const isGoogleAuth = currentUser?.providerData?.some(provider => provider.providerId === 'google.com');
  
  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser?.uid) {
        try {
          setIsLoading(true);
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setUserName(userData.displayName || '');
          }
          
          // Initialize email field with current user's email
          if (currentUser.email) {
            setNewEmail(currentUser.email);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchUserData();
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      setIsLoading(true);
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    } finally {
      setIsLoading(false);
      setShowLogoutModal(false);
    }
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    if (currentUser?.uid) {
      try {
        setIsLoading(true);
        
        // Check if email has been changed
        const emailChanged = newEmail !== currentUser.email;
        
        // Update user profile in Firestore
        const userDocRef = doc(db, 'users', currentUser.uid);
        
        // Check if the document exists first
        const docSnap = await getDoc(userDocRef);
        
        if (docSnap.exists()) {
          // Update existing document with new display name
          await updateDoc(userDocRef, {
            displayName: userName,
            updatedAt: new Date()
          });
        } else {
          // Create new document with user ID, email and display name
          await updateDoc(userDocRef, {
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: userName,
            createdAt: new Date(),
            updatedAt: new Date()
          });
        }
        
        // Handle email update if changed
        if (emailChanged && !isGoogleAuth) {
          // Show password modal for reauthentication
          setShowEmailPasswordModal(true);
        } else {
          // Show success toast notification for profile update only
          showSuccess('Profile information updated successfully!');
        }
      } catch (error) {
        console.error('Error saving user information:', error);
        showError('Failed to update profile information: ' + error.message);
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  // Function to handle email update after password verification
  const handleEmailUpdate = async () => {
    try {
      setIsLoading(true);
      
      // Reauthenticate user first
      await reauthenticate(emailPasswordVerify);
      
      // Then update email
      await updateUserEmail(newEmail);
      
      // Close the modal
      setShowEmailPasswordModal(false);
      
      // Reset password field
      setEmailPasswordVerify('');
      
      // Show email verification modal
      setShowEmailVerificationModal(true);
    } catch (error) {
      console.error('Error updating email:', error);
      showError('Failed to update email: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to validate password meets all requirements
  const validatePassword = (password) => {
    return password.length >= 6 && 
           /[A-Z]/.test(password) && 
           /[a-z]/.test(password) && 
           /[0-9]/.test(password);
  };

  const handleSavePassword = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (newPassword !== confirmPassword) {
      showError('Passwords do not match');
      return;
    }
    
    if (!validatePassword(newPassword)) {
      showError('Password does not meet requirements');
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Reauthenticate user first
      await reauthenticate(currentPassword);
      
      // Then update password
      await updateUserPassword(newPassword);
      
      // Reset form fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      showSuccess('Password updated successfully!');
    } catch (error) {
      console.error('Error updating password:', error);
      showError('Failed to update password: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container py-4">
      {isLoading && <LoadingSpinner />}
      
      <div className="row mb-3">
        <div className="col">
          <h2 className="mb-0">My Account</h2>
          <p className="text-muted">Manage your account settings and billing.</p>
        </div>
        <div className="col-auto">
          <button 
            className="btn btn-outline-light" 
            onClick={() => setShowLogoutModal(true)}
            style={{ transition: 'all 0.3s ease' }}
            onMouseOver={(e) => e.currentTarget.classList.replace('btn-outline-light', 'btn-primary')}
            onMouseOut={(e) => e.currentTarget.classList.replace('btn-primary', 'btn-outline-light')}
          >
            Sign Out <i className="bi bi-box-arrow-right ms-1"></i>
          </button>
        </div>
      </div>
      
      {/* Custom Tabs */}
      <div className="mb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
        <div className="d-flex" style={{ position: 'relative' }}>
          <div style={{ position: 'relative', marginRight: '2rem' }}>
            <button 
              className="custom-tab"
              style={{ 
                background: 'transparent',
                border: 'none',
                color: activeTab === 'settings' ? '#0d6efd' : 'rgba(255,255,255,0.75)',
                padding: '0.5rem 0',
                marginBottom: '0.5rem',
                fontWeight: activeTab === 'settings' ? '500' : '400',
                cursor: 'pointer'
              }}
              onClick={() => setActiveTab('settings')}
              onMouseOver={(e) => e.target.style.color = activeTab !== 'settings' ? '#fff' : '#0d6efd'}
              onMouseOut={(e) => e.target.style.color = activeTab === 'settings' ? '#0d6efd' : 'rgba(255,255,255,0.75)'}
            >
              Settings
            </button>
            {activeTab === 'settings' && (
              <div style={{ 
                position: 'absolute', 
                bottom: 0, 
                left: 0, 
                width: '100%', 
                height: '3px', 
                backgroundColor: '#0d6efd' 
              }}></div>
            )}
          </div>
          
          <div style={{ position: 'relative' }}>
            <button 
              className="custom-tab"
              style={{ 
                background: 'transparent',
                border: 'none',
                color: activeTab === 'billing' ? '#0d6efd' : 'rgba(255,255,255,0.75)',
                padding: '0.5rem 0',
                marginBottom: '0.5rem',
                fontWeight: activeTab === 'billing' ? '500' : '400',
                cursor: 'pointer'
              }}
              onClick={() => setActiveTab('billing')}
              onMouseOver={(e) => e.target.style.color = activeTab !== 'billing' ? '#fff' : '#0d6efd'}
              onMouseOut={(e) => e.target.style.color = activeTab === 'billing' ? '#0d6efd' : 'rgba(255,255,255,0.75)'}
            >
              Billing
            </button>
            {activeTab === 'billing' && (
              <div style={{ 
                position: 'absolute', 
                bottom: 0, 
                left: 0, 
                width: '100%', 
                height: '3px', 
                backgroundColor: '#0d6efd' 
              }}></div>
            )}
          </div>
        </div>
      </div>
      
      {/* Settings Tab Content */}
      {activeTab === 'settings' && (
        <div>
          {/* Basic Information */}
          <div className="card bg-dark mb-4">
            <div className="card-body">
              <h5 className="card-title mb-4 text-light">Basic Information</h5>
              <form onSubmit={handleSaveChanges}>
                <div className="mb-3 row">
                  <label htmlFor="name" className="col-sm-3 col-form-label text-light">Name:</label>
                  <div className="col-sm-9">
                    <input 
                      type="text" 
                      className="form-control" 
                      id="name" 
                      placeholder="Your name"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                    />
                    <small className="text-muted">(Optional)</small>
                  </div>
                </div>
                <div className="mb-3 row">
                  <label htmlFor="email" className="col-sm-3 col-form-label text-light">Email address:</label>
                  <div className="col-sm-9">
                    <input 
                      type="email" 
                      className="form-control" 
                      id="email" 
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      disabled={isGoogleAuth}
                      readOnly={isGoogleAuth}
                    />
                    {isGoogleAuth ? (
                      <small className="text-warning d-block mt-1">
                        <i className="bi bi-google me-1"></i>
                        You are authenticated with Google. Contact support if you need to update your email authentication method.
                      </small>
                    ) : (
                      <small className="text-muted d-block mt-1">Email changes require verification. A confirmation email will be sent to your new address.</small>
                    )}
                  </div>
                </div>
                <div className="text-end">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
          
          {/* Change Password - Only show if not using Google auth */}
          {!isGoogleAuth && (
            <div className="card bg-dark">
              <div className="card-body">
                <h5 className="card-title mb-4 text-light">Change Your Password</h5>
                <form onSubmit={handleSavePassword}>
                  <div className="mb-3 row">
                    <label htmlFor="currentPassword" className="col-sm-3 col-form-label text-light">Current password:</label>
                    <div className="col-sm-9">
                      <input 
                        type="password" 
                        className="form-control" 
                        id="currentPassword"
                        placeholder="Enter current password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="mb-3 row">
                    <label htmlFor="newPassword" className="col-sm-3 col-form-label text-light">New password:</label>
                    <div className="col-sm-9">
                      <input 
                        type="password" 
                        className={`form-control ${newPassword && newPassword.length < 6 ? 'is-invalid' : ''}`} 
                        id="newPassword"
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      {newPassword && (
                        <div className="mt-2">
                          <div className="small mb-1 text-white">Password must:</div>
                          <ul className="small ps-3 mb-0">
                            <li className={newPassword.length >= 6 ? 'text-success' : 'text-danger'}>
                              Be at least 6 characters long
                            </li>
                            <li className={/[A-Z]/.test(newPassword) ? 'text-success' : 'text-danger'}>
                              Include at least one uppercase letter
                            </li>
                            <li className={/[a-z]/.test(newPassword) ? 'text-success' : 'text-danger'}>
                              Include at least one lowercase letter
                            </li>
                            <li className={/[0-9]/.test(newPassword) ? 'text-success' : 'text-danger'}>
                              Include at least one number
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mb-3 row">
                    <label htmlFor="confirmPassword" className="col-sm-3 col-form-label text-light">Confirm password:</label>
                    <div className="col-sm-9">
                      <input 
                        type="password" 
                        className={`form-control ${confirmPassword && newPassword !== confirmPassword ? 'is-invalid' : ''}`} 
                        id="confirmPassword"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                      {confirmPassword && newPassword !== confirmPassword && (
                        <div className="invalid-feedback d-block">
                          Passwords do not match
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-end">
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={isLoading || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword || !validatePassword(newPassword)}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Updating...
                        </>
                      ) : 'Save Password'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Billing Tab Content */}
      {activeTab === 'billing' && (
        <div className="card bg-dark">
          <div className="card-body">
            <h5 className="card-title mb-4 text-light">Billing Information</h5>
            <p className="text-muted">Billing features coming soon.</p>
          </div>
        </div>
      )}
      
      {/* Logout Confirmation Modal */}
      <BaseModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        title="Confirm Sign Out"
        maxWidth="400px"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setShowLogoutModal(false)}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary ms-2"
              onClick={handleLogout}
              disabled={isLoading}
            >
              {isLoading ? 'Signing out...' : 'Sign Out'}
            </button>
          </>
        }
      >
        <p>Are you sure you want to sign out?</p>
      </BaseModal>
      
      {/* Email Password Verification Modal */}
      <BaseModal
        isOpen={showEmailPasswordModal}
        onClose={() => {
          setShowEmailPasswordModal(false);
          setEmailPasswordVerify('');
          setNewEmail(currentUser?.email || '');
        }}
        title="Verify Your Password"
        maxWidth="550px"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowEmailPasswordModal(false);
                setEmailPasswordVerify('');
                setNewEmail(currentUser?.email || '');
              }}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary ms-2"
              onClick={handleEmailUpdate}
              disabled={isLoading || !emailPasswordVerify}
              id="continueEmailUpdateBtn"
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Verifying...
                </>
              ) : 'Continue'}
            </button>
          </>
        }
      >
        <div className="text-center mb-3">
          <i className="bi bi-shield-lock" style={{ fontSize: '3rem', color: '#0d6efd' }}></i>
        </div>
        <p>You're changing your email address to: <strong>{newEmail}</strong></p>
        <p>For security reasons, please enter your current password to continue:</p>
        <form onSubmit={(e) => {
          e.preventDefault();
          if (emailPasswordVerify) {
            handleEmailUpdate();
          }
        }}>
          <div className="mb-3">
            <input
              type="password"
              className="form-control"
              placeholder="Enter your current password"
              value={emailPasswordVerify}
              onChange={(e) => setEmailPasswordVerify(e.target.value)}
              autoFocus
            />
          </div>
          {/* Hidden submit button to enable form submission */}
          <button type="submit" style={{ display: 'none' }}></button>
        </form>
      </BaseModal>
      
      {/* Email Verification Modal */}
      <BaseModal
        isOpen={showEmailVerificationModal}
        onClose={() => setShowEmailVerificationModal(false)}
        title="Email Verification Required"
        maxWidth="550px"
        footer={
          <button
            className="btn btn-primary"
            onClick={() => setShowEmailVerificationModal(false)}
          >
            Understood
          </button>
        }
      >
        <div className="text-center mb-3">
          <i className="bi bi-envelope-check" style={{ fontSize: '3rem', color: '#0d6efd' }}></i>
        </div>
        <p>An email has been sent to <strong>{newEmail}</strong> to confirm this change.</p>
        <p>Your email will remain <strong>{currentUser?.email}</strong> until this change is confirmed.</p>
        <p className="mb-0">Please check your inbox and click the verification link to complete the email update.</p>
      </BaseModal>
    </div>
  );
};

export default Profile;
